import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { StatusBadge } from "../TrackPage";
import { inputCls } from "../Login";

const STATUSES = ["", "Order Created", "Pickup Scheduled", "Picked Up", "In Transit", "Reached Hub",
  "Out for Delivery", "Delivered", "NDR", "RTO Initiated", "RTO In Transit", "RTO Delivered", "Cancelled"];

export default function Shipments() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState({});
  const [pendingOnly, setPendingOnly] = useState(false);
  const limit = 15;

  useEffect(() => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (pendingOnly) params.set("unmanifested", "true");
    api.get(`/shipments?${params}`).then((r) => setData(r.data)).catch(() => {});
  }, [status, q, page, dateFrom, dateTo, pendingOnly]);

  const toggleSel = (id) => setSelected((p) => { const nx = { ...p }; if (nx[id]) delete nx[id]; else nx[id] = true; return nx; });
  const selCount = Object.keys(selected).length;
  const allChecked = data.items.length > 0 && data.items.every((s) => selected[s.id]);
  const toggleAll = () => setSelected(allChecked ? {} : Object.fromEntries(data.items.map((s) => [s.id, true])));

  const downloadManifest = async () => {
    const ids = Object.keys(selected);
    try {
      const res = ids.length
        ? await api.post("/reports/manifest.pdf", { ids }, { responseType: "blob" })
        : await api.get(`/reports/manifest.pdf?date=${dateFrom || new Date().toISOString().slice(0, 10)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `INDIAFAST-manifest-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(ids.length ? `Manifest generated for ${ids.length} selected shipment(s)` : "Day-end manifest downloaded");
      setSelected({});
      setPage(1);
    } catch (err) { toast.error(err.response?.data?.detail || "Manifest download failed"); }
  };

  const downloadManifestOld = null;

  const pages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div data-testid="shipments-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">Shipments</h1>
          <p className="mt-1 text-sm text-slate-500">{data.total} total</p>
        </div>
        <Link to="/app/book" className="rounded-full bg-saffron px-5 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover">+ Book Shipment</Link>
      </div>

      <div className="flex flex-wrap gap-3">
        <input data-testid="shipments-search" className={`${inputCls} max-w-xs`} placeholder="Search AWB / Order ID / recipient"
               value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <select data-testid="shipments-status-filter" className={`${inputCls} max-w-xs`} value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All Statuses"}</option>)}
        </select>
        <input type="date" className={`${inputCls} max-w-[150px]`} data-testid="shipments-date-from" value={dateFrom}
               onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        <input type="date" className={`${inputCls} max-w-[150px]`} data-testid="shipments-date-to" value={dateTo}
               onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
        <button onClick={() => { setPendingOnly(!pendingOnly); setPage(1); }} data-testid="agent-pending-manifest-toggle"
                className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${pendingOnly ? "border-amber-400 bg-amber-50 text-amber-600" : "border-slate-300 text-slate-500 hover:border-amber-400"}`}>
          Pending Manifest
        </button>
        <button onClick={downloadManifest} data-testid="agent-manifest-btn"
                className="rounded-full bg-navy px-4 py-2 text-xs font-bold text-white hover:bg-express">
          {selCount ? `Generate Manifest (${selCount})` : "Day-End Manifest PDF"}
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="shipments-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-3 py-3.5"><input type="checkbox" data-testid="agent-manifest-select-all" checked={allChecked} onChange={toggleAll} className="h-4 w-4 accent-saffron" /></th>
              <th className="px-5 py-3.5">Date</th><th className="px-5 py-3.5">Order / AWB</th><th className="px-5 py-3.5">Receiver (Consignee)</th><th className="px-5 py-3.5">Country</th>
              <th className="px-5 py-3.5">Courier</th><th className="px-5 py-3.5">Type</th>
              <th className="px-5 py-3.5">Payment</th><th className="px-5 py-3.5">Price</th><th className="px-5 py-3.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <td className="px-3 py-3.5"><input type="checkbox" data-testid={`manifest-check-${s.awb}`} checked={!!selected[s.id]} onChange={() => toggleSel(s.id)} className="h-4 w-4 accent-saffron" /></td>
                <td className="whitespace-nowrap px-5 py-3.5 text-xs font-semibold text-slate-600" data-testid={`shipment-date-${s.awb}`}>{new Date(s.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                <td className="px-5 py-3.5">
                  <Link to={`/app/shipments/${s.id}`} className="font-mono font-semibold text-express hover:underline">{s.awb}</Link>
                  {s.manifested ? <span className="ml-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" title={s.manifest_no || "Manifested"}>MF</span> : null}
                  <p className="font-mono text-[11px] text-slate-400">{s.order_id}</p>
                </td>
                <td className="px-5 py-3.5" data-testid={`agent-receiver-cell-${s.awb}`}>
                  {s.delivery?.name || "—"}
                  {s.delivery?.city ? <span className="block text-[11px] text-slate-400">{s.delivery.city}</span> : null}
                </td>
                <td className="px-5 py-3.5 font-semibold" data-testid={`agent-country-cell-${s.awb}`}>{s.delivery?.country || s.destination_country || "India"}</td>
                <td className="px-5 py-3.5">{s.courier_name}</td>
                <td className="px-5 py-3.5">
                  <span data-testid={`shipment-type-${s.awb}`} className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${s.is_international ? "bg-blue-100 text-express" : "bg-emerald-50 text-emerald-700"}`}>
                    {s.is_international ? "INTERNATIONAL" : "DOMESTIC"}
                  </span>
                </td>
                <td className="px-5 py-3.5">{s.payment_type}</td>
                <td className="px-5 py-3.5 font-semibold">₹{s.customer_price}</td>
                <td className="px-5 py-3.5"><StatusBadge status={s.status} /></td>
              </tr>
            ))}
            {!data.items.length && (
              <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-400" data-testid="shipments-empty">No shipments found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-2">
          {Array.from({ length: pages }, (_, i) => (
            <button key={i} onClick={() => setPage(i + 1)} data-testid={`page-btn-${i + 1}`}
                    className={`h-9 w-9 rounded-lg text-sm font-bold ${page === i + 1 ? "bg-saffron text-white" : "border border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
